import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { AmlRisk } from '@app/core/state/types/aml.types';
import { BlFrontendButtonComponent } from '@app/core/wrappers/bl-frontend-button.component';
import { CalendarComponent } from '@app/shared/components/calendar/calendar.component';
import { DialogForm, DialogResult } from '@app/shared/components/form-dialog-base/form-dialog-base.component';
import { TodayDate } from '@app/shared/misc/today.date';
import { TranslocoModule } from '@jsverse/transloco';
import { DropdownModule } from 'primeng/dropdown';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { Observable } from 'rxjs';
import { getTranslationProvider } from 'src/app/core/i18n/getTranslationProvider';
import { TranslatableFormDialogBaseComponent } from 'src/app/core/i18n/translatable-form-dialog-base.component';

interface Result extends DialogResult {
  risk: AmlRisk;
  date: string;
}

interface Form extends DialogForm<unknown> {
  risk: FormControl<AmlRisk>;
  date: FormControl<string>;
}

const TRANSLATION_SCOPE = 'createManualAmlDialogComponent';

@Component({
  template: `
    <ng-container *transloco="let t; prefix: scope">
      <form [formGroup]="form" (ngSubmit)="onSubmit()" pFocusTrap>
        <label for="risk" class="mb-0">{{ t('riskAssessment') }}</label>
        <p-dropdown
          id="risk"
          formControlName="risk"
          [options]="amlRiskOptions$ | async"
          optionLabel="label"
          optionValue="value"
          [placeholder]="t('selectRiskLevel')"
          [style]="{ 'min-width': '100%' }"></p-dropdown>

        <label for="date" class="mt-6">{{ t('date') }}</label>
        <app-calendar
          id="date"
          formControlName="date"
          [style]="{ 'max-width': '100%', display: 'flex' }"
          appendTo="body"></app-calendar>

        <div class="flex flex-row w-full justify-end mt-8">
          <bl-frontend-button
            type="button"
            variant="secondary"
            (onclick)="close(null)"
            [text]="'shared.cancel' | transloco"></bl-frontend-button>
          <bl-frontend-button
            type="submit"
            variant="primary"
            class="ml-2"
            [disabled]="form.invalid"
            [text]="'shared.save' | transloco"></bl-frontend-button>
        </div>
      </form>
    </ng-container>
  `,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [getTranslationProvider(TRANSLATION_SCOPE)],
  imports: [
    CommonModule,
    DropdownModule,
    ReactiveFormsModule,
    CalendarComponent,
    BlFrontendButtonComponent,
    TranslocoModule,
  ],
})
export class CreateManualAmlDialogComponent extends TranslatableFormDialogBaseComponent<null, Result, Form> {
  amlRiskOptions$: Observable<{ label: string; value: AmlRisk }[]>;

  private amlRiskOptionsTemplates = [
    { label: 'low', value: AmlRisk.LOW },
    { label: 'normal', value: AmlRisk.NORMAL },
    { label: 'high', value: AmlRisk.HIGH },
  ];

  protected override onInitialized = () => {
    this.amlRiskOptions$ = this.onTranslationChangeMapTo((translation: Record<string, string>) => {
      return this.amlRiskOptionsTemplates.map((option) => ({
        ...option,
        label: translation[option.label],
      }));
    });

    this.form = this.builder.group({
      risk: new FormControl(null, [Validators.required]),
      date: new FormControl(TodayDate.getIsoFormattedDate(new Date())),
    });
  };

  protected static override getTranslationScope(): string {
    return TRANSLATION_SCOPE;
  }

  protected static override getDialogConfig(translation: Record<string, string>): Omit<DynamicDialogConfig, 'data'> {
    return {
      header: translation.addRiskAssessment,
      width: '350px',
      height: 'auto',
      modal: true,
    };
  }

  onSubmit() {
    const { risk, date } = this.form.value;
    this.close({ risk, date });
  }
}
